/**
 * NAA (Nested App Authentication) helper — Phase 1.
 *
 * Acquires a Microsoft access token for our exposed API scope using MSAL's
 * nestable public client (brokered by Outlook), then POSTs it to the backend
 * /outlook/auth/exchange for cryptographic validation. Silent-first, with a
 * one-time interactive popup on InteractionRequiredAuthError.
 *
 * Kept out of the live task-pane flow (main.js) — driven only from the dev
 * page in Phase 1.
 */
import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

const AUTHORITY = "https://login.microsoftonline.com/common";

let pca = null;

/** True when the host supports NAA. */
export function isNaaSupported() {
  return Office.context.requirements.isSetSupported("NestedAppAuth", "1.1");
}

async function getClientId() {
  const resp = await fetch("/outlook/auth/config");
  if (!resp.ok) throw new Error("naa/config failed: " + resp.status);
  const { clientId } = await resp.json();
  if (!clientId) throw new Error("NAA clientId is not configured on the server");
  return clientId;
}

async function getApp() {
  if (pca) return pca;
  const clientId = await getClientId();
  pca = await createNestablePublicClientApplication({
    auth: { clientId, authority: AUTHORITY },
    cache: { cacheLocation: "localStorage" },
  });
  return pca;
}

/** Acquire a Microsoft access token for our exposed API scope. */
export async function acquireMsToken() {
  const app = await getApp();
  const clientId = await getClientId();
  const request = { scopes: [`api://${clientId}/access_as_user`] };

  const account = app.getActiveAccount() || app.getAllAccounts()[0];
  console.debug("[NAA] acquireTokenSilent…", { hasAccount: !!account, scopes: request.scopes });
  try {
    const result = await app.acquireTokenSilent({ ...request, account });
    console.debug("[NAA] silent token acquired");
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      console.warn("[NAA] silent failed (interaction required) — trying popup", err);
      const result = await app.acquireTokenPopup(request);
      console.debug("[NAA] popup token acquired");
      return result.accessToken;
    }
    console.error("[NAA] acquireTokenSilent failed (non-interaction):", err);
    throw err;
  }
}

/** Exchange the Microsoft token for a LeapFILE session. Returns {status, body}. */
export async function exchangeToken() {
  const token = await acquireMsToken();
  const resp = await fetch("/outlook/auth/exchange", {
    method: "POST", headers: { Authorization: `Bearer ${token}` },
  });
  return { status: resp.status, body: await resp.json().catch(() => ({})) };
}

/** Bind the Microsoft identity to a just-logged-in LeapFILE session. */
export async function linkIdentity(sessionOid) {
  const token = await acquireMsToken();
  const resp = await fetch("/outlook/auth/link", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ session: sessionOid }),
  });
  return resp.ok;
}
